/** The chat's polls: each question with its answers as bars, the winner lit up. */

import { Fit, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Poll } from "../../types";
import { shortDate } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1010, y: 300, r: 160, seed: 40.3, wob: 0.22 },
  { colour: "blurple", x: 50, y: 1800, r: 250, seed: 41.6, wob: 0.18 },
];

const TOP = 400;
const ROOM = 1400; // from below the heading to above the arrows, on the 1920 stage
const GAP = 26;
const HEAD = 150; // the question and who asked
const ANSWER = 86;

export const pollHeight = (poll: Poll) => HEAD + poll.answers.length * ANSWER + 34;

export function Polls({ polls }: { polls: Poll[] }) {
  const extra = useStageExtra();
  const heights = polls.map(pollHeight);
  const total = heights.reduce((a, b) => a + b, 0) + GAP * (polls.length - 1);
  // Many polls shrink to fit; a taller phone gives them more room first.
  const room = ROOM + extra;
  const scale = Math.min(1, room / total);
  const tops = heights.map((_, i) => heights.slice(0, i).reduce((a, b) => a + b + GAP, 0));

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.rise("[data-part=lead]", 0.45, { y: 30, sound: false });
    s.q(".poll").forEach((poll, i) => {
      const at = 0.6 + i * 0.3;
      s.tl.fromTo(poll, { x: 1000, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.55, ease: "power4.out" }, at);
      s.sound("whoosh", at, 1 + i * 0.08, { pan: 0.6, length: 0.4 });
      s.tl.fromTo(poll.querySelectorAll(".poll-fill"), { scaleX: 0 }, { scaleX: 1, duration: 0.6, stagger: 0.08, ease: "power3.out" }, at + 0.35);
      s.sound("zip", at + 0.35, 1 + i * 0.1, { length: 0.6 });
      s.pop(poll.querySelectorAll(".won .poll-row"), at + 0.95, { from: 0.7, dur: 0.4 });
    });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick">The chat's polls</div>
          <div data-part="lead" className="lead">
            {polls.length === 1 ? "One question, put to a vote" : `${polls.length} questions, put to a vote`}
          </div>
        </Zone>
        <Zone at="top">
          <div className="abs" style={{ left: 0, top: TOP, width: 1080, height: total, transform: `scale(${scale})`, transformOrigin: "50% 0" }}>
            {polls.map((poll, i) => {
              const most = Math.max(...poll.answers.map((a) => a.votes), 0);
              return (
                <div key={poll.at + poll.question} className="poll" style={{ top: tops[i] }}>
                  <Fit size={54} min={34} className="disp" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {poll.question}
                  </Fit>
                  <div className="mono" style={{ marginTop: 12, fontSize: 24, fontWeight: 700, color: "var(--wr-muted)" }}>
                    ASKED BY {poll.by.toUpperCase()} · {shortDate(poll.at).toUpperCase()}
                  </div>
                  {poll.answers.map((answer) => {
                    // Every answer tied for the most votes won.
                    const won = most > 0 && answer.votes === most;
                    return (
                      <div key={answer.text} className={`poll-answer${won ? " won" : ""}`}>
                        <div className="poll-row">
                          <span>{answer.text}</span>
                          <em>{answer.votes}</em>
                        </div>
                        <div className="poll-bar">
                          <div className="poll-fill" style={{ width: `${most ? (answer.votes / most) * 100 : 0}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
